package signSymptom.components

import antd.button.button
import antd.comment.comment
import antd.descriptions.descriptions
import antd.descriptions.descriptionsItem
import antd.form.form
import antd.form.formItem
import antd.input.input
import antd.list.ListComponent
import antd.list.list
import antd.message.message
import antd.modal.modal
import antd.pageheader.pageHeader
import antd.tabs.tabPane
import antd.tabs.tabs
import com.jingfangpai.api.clients.AcupointApiClient
import com.jingfangpai.api.clients.MedicineApiClient
import com.jingfangpai.api.clients.PrescriptionApiClient
import com.jingfangpai.common.data.model.IdName
import com.jingfangpai.common.orm.mapping.SignSymptom
import com.jingfangpai.common.orm.mapping.Comment
import com.jingfangpai.common.orm.mapping.enums.CommentEntityType
import com.jingfangpai.common.orm.mapping.enums.CommentType
import com.jingfangpai.web.components.ImageManager
import external.ckEditor.CKEditor
import external.ckEditor.ClassicEditor
import external.ckEditor.EditorConf
import external.ckEditor.EditorToolbarConf
import external.toJsObject
import kotlinext.js.Object
import kotlinext.js.js
import kotlinext.js.jsObject
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLScriptElement
import react.*
import react.dom.a
import react.dom.div
import react.dom.li
import react.router.dom.useHistory
import setTitle
import signSymptom.containers.SignSymptomDetailPageContext
import antd.avatar.avatar
import antd.card.card
import antd.icon.editOutlined
import antd.icon.userOutlined
import com.jingfangpai.common.data.type.DateTime
import kotlinx.html.style
import react.dom.jsStyle
import react.dom.svg
import user.service.AuthService
import com.jingfangpai.common.orm.mapping.enums.UserPermissionSignSymptom
import kotlinx.html.onClick
import kotlinx.html.unsafe
import react.dom.span
import user.service.CurrentUserPermission

external interface SignSymptomDetailProps : RProps {
    var id:String
}

val SignSymptomDetail = functionalComponent<SignSymptomDetailProps> { props ->

    val ctx = useContext(SignSymptomDetailPageContext)
    val history = useHistory()
    val (signSymptom,setSignSymptom) = useState(SignSymptom())
    val (notes, setNotes) = useState(arrayOf<Comment>())
    val (showNoteModal, setShowNoteModal) = useState(false)
    val (editingNote, setEditingNote) = useState<Comment?>(null)
    val (noteForm, setNoteForm) = useState(Comment())
    val (saving, setSaving) = useState(false)
    val (saveError, setSaveError) = useState<String?>(null)
    val (medicineIdNames, setMedicineIdNames) = useState<Map<String, String>>(emptyMap())
    val (prescriptionIdNames, setPrescriptionIdNames) = useState<Map<String, String>>(emptyMap())
    val (acupointIdNames, setAcupointIdNames) = useState<Map<String, String>>(emptyMap())
    val (editorScriptLoaded, setEditorScriptLoaded) = useState(false)
    val (noteEditor, setNoteEditor) = useState<CKEditor?>(null)

    fun parseDescription(description: String): List<Pair<String, String>> {
        val sections = mutableListOf<Pair<String, String>>()
        val regex = Regex("【([^】]+)】")
        val matches = regex.findAll(description).toList()
        
        for (i in matches.indices) {
            val title = matches[i].groupValues[1]
            val startIdx = matches[i].range.last + 1
            val endIdx = if (i < matches.size - 1) matches[i + 1].range.first else description.length
            val content = description.substring(startIdx, endIdx).trim()
            sections.add(title to content)
        }
        
        return sections
    }

    fun makeTextClickable(text: String): String {
        // Sort by length (longest first) to avoid partial matches
        val allEntities = mutableListOf<Triple<String, String, String>>()
        medicineIdNames.forEach { (name, id) ->
            allEntities.add(Triple(name, id, "medicine"))
        }
        prescriptionIdNames.forEach { (name, id) ->
            allEntities.add(Triple(name, id, "prescription"))
        }
        acupointIdNames.forEach { (name, id) ->
            allEntities.add(Triple(name, id, "acupoint"))
        }
        
        val sortedEntities = allEntities.sortedByDescending { it.first.length }
        
        // Find all non-overlapping matches
        data class Match(val start: Int, val end: Int, val html: String)
        val matches = mutableListOf<Match>()
        
        sortedEntities.forEach { (name, id, type) ->
            if (name.isBlank() || id.isBlank()) return@forEach
            val url = when (type) {
                "medicine" -> SmartTcmRoute.MedicineDetail.link.url.replace(":id", id)
                "prescription" -> SmartTcmRoute.PrescriptionDetail.link.url.replace(":id", id)
                "acupoint" -> LearningRoute.AcupointDetail.link.url.replace(":id", id)
                else -> ""
            }
            
            if (url.isNotEmpty()) {
                val linkHtml = "<a href=\"$url\" target=\"_blank\" style=\"color: #1890ff; text-decoration: underline; cursor: pointer;\">$name</a>"
                val escapedName = Regex.escape(name)
                val regex = Regex(escapedName, RegexOption.IGNORE_CASE)
                
                regex.findAll(text).forEach { matchResult ->
                    val startIndex = matchResult.range.first
                    val endIndex = matchResult.range.last + 1
                    
                    // Check if this position overlaps with existing matches
                    val overlaps = matches.any {
                        !(startIndex >= it.end || endIndex <= it.start)
                    }
                    
                    if (!overlaps) {
                        matches.add(Match(startIndex, endIndex, linkHtml))
                    }
                }
            }
        }
        
        // Sort matches by start position
        matches.sortBy { it.start }
        
        // Build result by concatenating segments
        val result = StringBuilder()
        var lastIndex = 0
        
        matches.forEach { match ->
            // Add text before this match
            if (match.start > lastIndex) {
                result.append(text.substring(lastIndex, match.start))
            }
            // Add the HTML link
            result.append(match.html)
            lastIndex = match.end
        }
        
        // Add remaining text
        if (lastIndex < text.length) {
            result.append(text.substring(lastIndex))
        }
        
        return result.toString()
    }

    useEffect(props.id) {
        if(32 == props.id?.length?:0) {
            // Fetch medicine, prescription, and acupoint ID names
            MedicineApiClient.getIdNameList({ resp ->
                if (resp.ok() && resp.data != null) {
                    val map = resp.data!!.filter { it.id != null }.associate { it.name!! to it.id!! }
                    setMedicineIdNames(map)
                }
            }, {})
            
            PrescriptionApiClient.getIdNameList({ resp ->
                if (resp.ok() && resp.data != null) {
                    val map = resp.data!!.filter { it.id != null }.associate { it.name!! to it.id!! }
                    setPrescriptionIdNames(map)
                }
            }, {})
            
            AcupointApiClient.listIdAndName({ resp ->
                if (resp.ok() && resp.data != null) {
                    val map = resp.data!!.filter { it.id != null }.associate { it.name!! to it.id!! }
                    setAcupointIdNames(map)
                }
            }, {})
            
            ctx.getDetail(props.id,{ resp->
                if(resp.ok() && resp.data !=null){
                    setSignSymptom(resp.data!!)
                    setTitle(resp.data!!.name?.getLiteral()?:"")
                }else{
                    message.error("获取体征症状失败")
                }
            },{
                    message.error("获取体征症状失败")
            })

            // Fetch notes for this sign symptom
            ctx.getComments(CommentEntityType.SignSymptom, props.id, CommentType.笔记, { resp ->
                if (resp.ok() && resp.data != null) {
                    setNotes(resp.data!!)
                }
            }, {
                console.error("Failed to fetch notes:", it)
            })
        }
    }

    // Base64 upload adapter for CKEditor (handles image paste)
    val configureBase64Upload = js("""
function(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = function(loader) {
        return {
            upload: function() {
                return loader.file.then(function(file) {
                    return new Promise(function(resolve) {
                        var reader = new FileReader();
                        reader.onload = function() { resolve({ 'default': reader.result }); };
                        reader.readAsDataURL(file);
                    });
                });
            }
        };
    };
}
""").unsafeCast<(dynamic) -> Unit>()

    // Load CKEditor script once
    useEffectOnce {
        val s = document.createElement("script") as HTMLScriptElement
        s.src = "/ckeditor.js"
        s.id = "note-editor-script"
        document.body?.appendChild(s)
        s.onload = {
            setEditorScriptLoaded(true)
        }
    }

    // Initialize / cleanup editor when modal visibility changes
    useEffect(showNoteModal, editorScriptLoaded) {
        if (showNoteModal && editorScriptLoaded) {
            // Wait for DOM to render the editor container
            js("setTimeout")({
                val el = document.querySelector("#note-editor")
                if (el != null) {
                    val config = jsObject<EditorConf> {
                        toolbar = jsObject<EditorToolbarConf> {
                            items = arrayOf(
                                "bold", "italic", "underline",
                                "fontSize", "fontColor", "link",
                                "bulletedList", "numberedList",
                                "|",
                                "outdent", "indent",
                                "|",
                                "imageUpload", "blockQuote",
                                "undo", "redo"
                            )
                        }
                        language = "zh-cn"
                        licenseKey = ""
                    }
                    ClassicEditor.create(el, config)
                        .then { editor: dynamic ->
                            configureBase64Upload(editor)
                            setNoteEditor(editor.unsafeCast<CKEditor>())
                            // Set initial content
                            noteForm.comment?.let { data ->
                                editor.setData(data)
                            }
                            // Sync editor content back to noteForm on change
                            editor.model.document.on("change:data") { _: dynamic, _: dynamic ->
                                val newComment = editor.getData() as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        comment = newComment
                                    }
                                }
                            }
                        }
                        .catch { error: dynamic ->
                            console.error("CKEditor init failed:", error)
                        }
                }
            }, 150)
        }
    }

    // Cleanup editor when modal closes
    fun destroyNoteEditor() {
        noteEditor?.let { editor ->
            try {
                editor.asDynamic().destroy()
            } catch (e: dynamic) {
                console.log("Editor destroy error:", e)
            }
            setNoteEditor(null)
        }
    }

    fun openAddNoteModal() {
        setEditingNote(null)
        setSaveError(null)
        setNoteForm(Comment().apply {
            entityType = CommentEntityType.SignSymptom
            entityId = props.id
            type = CommentType.笔记
            authorId = AuthService.getUserAuthData()?.id ?: ""
            createdBy = AuthService.getUserAuthData()?.userName ?: ""
            weight = 0
        })
        setShowNoteModal(true)
    }

    fun openEditNoteModal(note: Comment) {
        setEditingNote(note)
        setSaveError(null)
        setNoteForm(Object.assign(Comment(), note))
        setShowNoteModal(true)
    }

    fun saveNote() {
        // Get latest content from editor
        val editorComment = noteEditor?.asDynamic()?.getData() as? String
        val commentToSave = editorComment ?: noteForm.comment
        
        if (commentToSave.isNullOrBlank()) {
            setSaveError("请填写笔记内容")
            return
        }

        // Create a copy with the latest editor content
        val finalNote = Object.assign(Comment(), noteForm).apply {
            comment = commentToSave
            entityType = CommentEntityType.SignSymptom
            entityId = props.id
            type = CommentType.笔记
            authorId = AuthService.getUserAuthData()?.id ?: ""
        }
        
        // Set createdOn timestamp for new notes
        if (finalNote.createdOn == null) {
            finalNote.createdOn = DateTime()
        }

        setSaving(true)
        setSaveError(null)
        destroyNoteEditor()
        ctx.saveComment(finalNote, { resp ->
            setSaving(false)
            if (resp.ok()) {
                // Refresh notes list
                ctx.getComments(CommentEntityType.SignSymptom, props.id, CommentType.笔记, { refreshResp ->
                    if (refreshResp.ok() && refreshResp.data != null) {
                        setNotes(refreshResp.data!!)
                    }
                    setShowNoteModal(false)
                    // Reset form after successful save
                    setNoteForm(Comment())
                    setEditingNote(null)
                }, {})
            } else {
                setSaveError("抱歉，出错了：${resp.message}")
            }
        }, {
            setSaving(false)
            console.log(it)
            setSaveError("保存失败，${it.message}")
        })
    }

    div("SignSymptomDetail") {
        attrs.jsStyle = kotlinext.js.js {
                margin = "0 auto"
                maxWidth = "1920px"
            }


        pageHeader {
            attrs{
                className = "signSymptom-detail-page-header-responsive"
                onBack = {
                    window.history.back()
                }
                title = "${signSymptom?.name?:""}"
                subTitle = ""
                extra = arrayOf(buildElement{
                    if(CurrentUserPermission.hasPerm(UserPermissionSignSymptom.修改症状)){
                        button{
                            attrs{
                                onClick = {
                                    history.push(SmartTcmRoute.SignSymptomEdit.link.url.replace(":id",signSymptom.id?:""))
                                }
                            }
                            +"编辑"
                        }
                    }else{
                        span{
                            +""
                        }
                    }
                })
            }
        }

        div {
            // Image Manager Section
            ImageManager {
                attrs {
                    entityType = com.jingfangpai.common.orm.mapping.enums.FileEntityType.SignSymptom
                    entityId = props.id
                    onImagesChanged = { images ->
                        console.log("SignSymptom images changed:", images.size)
                    }
                }
            }
            
            // Basic Information Section
            card {
                attrs {
                    title = "基本信息"
                    style = kotlinext.js.js {
                        marginBottom = "16px"
                    }
                }

                descriptions {
                    attrs {
                        bordered = true
                        column = 2
                    }

                    val name = signSymptom.name?.getLiteral()
                    if (!name.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "名称"
                                span = 2
                            }
                            +name
                        }
                    }

                    val description = signSymptom.description
                    if (!description.isNullOrBlank()) {
                        descriptionsItem {
                            attrs {
                                label = "描述"
                                span = 2
                            }
                            div {
                                attrs.jsStyle = kotlinext.js.js {
                                    lineHeight = "1.8"
                                }
                                
                                val sections = parseDescription(description)
                                if (sections.isNotEmpty()) {
                                    sections.forEach { (title, content) ->
                                        div {
                                            attrs.jsStyle = kotlinext.js.js {
                                                marginBottom = "16px"
                                            }
                                            // Section title
                                            div {
                                                attrs.jsStyle = kotlinext.js.js {
                                                    fontWeight = "bold"
                                                    fontSize = "15px"
                                                    color = "#1890ff"
                                                    marginBottom = "8px"
                                                    paddingBottom = "4px"
                                                    borderBottom = "1px solid #e8e8e8"
                                                }
                                                +"【$title】"
                                            }
                                            // Section content
                                            div {
                                                attrs.jsStyle = kotlinext.js.js {
                                                    whiteSpace = "pre-wrap"
                                                    color = "rgba(0, 0, 0, 0.85)"
                                                    paddingLeft = "8px"
                                                }
                                                attrs["dangerouslySetInnerHTML"] = js { __html = makeTextClickable(content) }
                                            }
                                        }
                                    }
                                } else {
                                    // Fallback for descriptions without sections
                                    attrs["dangerouslySetInnerHTML"] = js { __html = makeTextClickable(description) }
                                }
                            }
                        }
                    }
                }
            }

            // Notes Section
            card {
                attrs {
                    title = "笔记"
                    extra = buildElement {
                        button {
                            attrs {
                                type = "primary"
                                onClick = {
                                    openAddNoteModal()
                                }
                            }
                            +"添加笔记"
                        }
                    }
                }

                div {
                    if (notes.isEmpty()) {
                        div {
                            +"暂无笔记"
                        }
                    } else {
                        list<Comment, ListComponent<Comment>> {
                            attrs {
                                className = "comment-list"
                                header = "${notes.size}条笔记"
                                itemLayout = "horizontal"
                                dataSource = notes
                                renderItem = { noteItem, index ->
                                    buildElement {
                                        li {
                                            comment {
                                                attrs {
                                                    author = noteItem.createdBy

                                                    content = buildElement {
                                                        div {
                                                            if (!noteItem.title.isNullOrBlank()) {
                                                                div {
                                                                    +noteItem.title!!
                                                                    attrs.jsStyle = kotlinext.js.js {
                                                                            fontWeight = "bold"
                                                                            marginBottom = "8px"
                                                                            display = "block"
                                                                        }

                                                                }
                                                            }
                                                            div {
                                                                attrs["dangerouslySetInnerHTML"] = js { __html = noteItem.comment ?: "" }
                                                                attrs.jsStyle = kotlinext.js.js {
                                                                        lineHeight = "1.6"
                                                                        fontSize = "14px"
                                                                        overflow = "hidden"
                                                                    }

                                                            }
                                                            if (!noteItem.description.isNullOrBlank()) {
                                                               div {
                                                                    +"（来源：${noteItem.description}）"
                                                                    attrs.jsStyle = kotlinext.js.js {
                                                                            marginTop = "8px"
                                                                            fontSize = "12px"
                                                                            color = "#666"
                                                                        }

                                                                }
                                                            }
                                                        }
                                                    }
                                                    datetime = noteItem.createdOn?.toDateTimeString()
                                                    actions = arrayOf(
                                                        buildElement {
                                                            button {
                                                                attrs {
                                                                    type = "link"
                                                                    size = "small"
                                                                    icon = buildElement {
                                                                        editOutlined {  }
                                                                    }
                                                                    onClick = {
                                                                        openEditNoteModal(noteItem)
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    )
                                                    attrs.avatar = buildElement {
                                                        avatar{
                                                            attrs {
                                                                style = kotlinext.js.js {
                                                                    backgroundColor = "#87d068"
                                                                }
                                                                shape = "square"
                                                                icon = buildElement {
                                                                    userOutlined {  }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Note Modal
        modal {
            attrs {
                title = if (editingNote != null) "编辑笔记" else "添加笔记"
                visible = showNoteModal
                maskClosable = false
                onCancel = {
                    if (!saving) {
                        destroyNoteEditor()
                        setSaveError(null)
                        setShowNoteModal(false)
                        // Reset form when closing modal
                        setNoteForm(Comment())
                        setEditingNote(null)
                    }
                }
                onOk = {
                    saveNote()
                }
                okText = if (saving) "保存中..." else "保存"
                cancelText = "取消"
                okButtonProps = kotlinext.js.js {
                    loading = saving
                    disabled = saving
                }
                width = 720
            }

            form {
                attrs {
                    layout = "vertical"
                }

                // Show error message if any
                if (saveError != null) {
                    div {
                        attrs.style = kotlinext.js.js {
                                color = "#ff4d4f"
                                backgroundColor = "#fff2f0"
                                border = "1px solid #ffccc7"
                                padding = "8px 12px"
                                marginBottom = "16px"
                                borderRadius = "4px"
                            }

                        +saveError!!
                    }
                }

                formItem {
                    attrs {
                        label = "笔记标题"
                    }
                    input {
                        attrs {
                            value = noteForm.title ?: ""
                            placeholder = "请填写笔记标题（可选）"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newTitle = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        title = newTitle
                                    }
                                }
                            }

                        }
                    }
                }

                formItem {
                    attrs {
                        label = "笔记内容"
                    }
                    div {
                        attrs.jsStyle = kotlinext.js.js {
                            border = "1px solid #d9d9d9"
                            borderRadius = "4px"
                            minHeight = "300px"
                        }
                        attrs["id"] = "note-editor"
                    }
                }

                formItem {
                    attrs {
                        label = "来源"
                    }
                    input {
                        attrs {
                            value = noteForm.description ?: ""
                            placeholder = "请填写笔记来源（可选）"
                            disabled = saving
                            onChange = { event: dynamic ->
                                val newDescription = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        description = newDescription
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
